import { useMemo } from 'react';

import { type WorkspaceMemberResponseWithRoles, WorkspaceMemberType } from '@air/api/types';

import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useWorkspaceRolesContext } from '~/providers/WorkspaceRolesProvider';
import { isActiveMember, isGuest } from '~/swr-hooks/members/utils/memberUtils';
import { useCurrentWorkspaceMember } from './useCurrentWorkspaceMember';
import { useFetchMembers } from './useFetchMembers';
import { sortMembersByRole } from './utils/sortMembersByRole';

export const useWorkspaceMembers = () => {
  const { data: members } = useFetchMembers();
  const { data: roles } = useWorkspaceRolesContext();
  const { data: subscription } = useSubscriptionContext();
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();

  const allMembers = useMemo(
    () =>
      members && roles
        ? sortMembersByRole(members, roles).filter(
            (member) => member.type === WorkspaceMemberType.member && !member.inactive,
          )
        : [],
    [members, roles],
  );

  const inactiveMembers = useMemo(
    () => (members && roles ? sortMembersByRole(members, roles).filter((member) => member.inactive) : []),
    [members, roles],
  );

  const activeMembers = useMemo(() => allMembers?.filter((member) => isActiveMember(member)), [allMembers]);

  const pendingMembers = useMemo(() => allMembers?.filter((member) => member.pending), [allMembers]);

  const billableMembers = useMemo(
    () => allMembers?.filter((member) => member.isBilled && !member.inactive),
    [allMembers],
  );

  const allGuests = useMemo(
    () => (members && roles ? sortMembersByRole(members, roles).filter((member) => isGuest(member)) : []),
    [members, roles],
  );

  const billableGuests = useMemo(() => allGuests?.filter((member) => member.isBilled), [allGuests]);

  const sysAdminMembers = useMemo(
    () =>
      (currentWorkspaceMember?.type !== WorkspaceMemberType.sysadmin
        ? []
        : members?.filter((member) => member.type === WorkspaceMemberType.sysadmin)) ?? [],
    [currentWorkspaceMember?.type, members],
  );

  const nonBillableMembers = useMemo(
    () => allMembers?.filter((member) => !member.isBilled && !member.inactive),
    [allMembers],
  );
  const nonBillableGuests = useMemo(() => allGuests?.filter((member) => !member.isBilled), [allGuests]);

  const allUsersWithMostPermissiveRole = useMemo<WorkspaceMemberResponseWithRoles[]>(() => {
    if (!members) {
      return [];
    }

    const billableMembersEmailsMap = new Map(billableMembers.map((m) => [m.email, m]));
    // Guests with 'Can Comment' or 'Can Edit' permissions are billable.
    const nonMemberBillableGuestsEmailsMap = new Map(
      billableGuests.filter((m) => !billableMembersEmailsMap.has(m.email)).map((m) => [m.email, m]),
    );

    const nonBillableMembersEmailsMap = new Map(
      nonBillableMembers.filter((m) => !nonMemberBillableGuestsEmailsMap.has(m.email)).map((m) => [m.email, m]),
    );

    const nonMemberNonBillableGuestsEmailsMap = new Map(
      nonBillableGuests
        .filter((m) => !billableMembersEmailsMap.has(m.email) && !nonBillableMembersEmailsMap.has(m.email))
        .map((m) => [m.email, m]),
    );

    return [
      ...Array.from(billableMembersEmailsMap.values()).map((m) => ({
        ...m,
        isBilled: true,
      })),
      ...Array.from(nonBillableMembersEmailsMap.values()).map((m) => ({
        ...m,
        isBilled: false,
      })),
      ...Array.from(nonMemberBillableGuestsEmailsMap.values()).map((m) => ({
        ...m,
        isBilled: true,
      })),
      ...Array.from(nonMemberNonBillableGuestsEmailsMap.values()).map((m) => ({
        ...m,
        isBilled: false,
      })),
    ].sort((a, b) => (a.isBilled === b.isBilled ? 0 : a.isBilled ? -1 : 1));
  }, [members, billableMembers, billableGuests, nonBillableMembers, nonBillableGuests]);

  const maxMembers = useMemo(() => subscription?.maxMembers, [subscription]);

  return {
    members: members
      ? {
          allMembers,
          activeMembers,
          inactiveMembers,
          pendingMembers,
          billableMembers,
          nonBillableMembers,

          allGuests,
          billableGuests,
          nonBillableGuests,

          sysAdminMembers,

          allUsersWithMostPermissiveRole,

          maxMembers,
        }
      : undefined,
  };
};
